import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import MedicalSafetyCard from './MedicalSafetyCard.jsx';
import './AthleteCard.css';

// One athlete, all their data tied together. Backend (/api/athletes/:id/card)
// scopes access: staff → any team athlete, parent → their child, athlete → self.
// `embedded` = rendered inside the AthleteProfile shell (which supplies its own
// back button + tabs), and `initialData` lets the shell pass the already-fetched
// card so we don't double-fetch.
function AthleteCard({ athleteId: propId, initialData, embedded }) {
  const params = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const id = propId || params.id;
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState('');
  const [acting, setActing] = useState('');

  const load = async () => {
    try {
      const res = await api.get(`${API_BASE_URL}/api/athletes/${id}/card`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.status === 403 ? "You don't have access to this athlete." : (err.response?.data?.error || 'Could not load this athlete.'));
    }
  };

  useEffect(() => {
    if (initialData) { setData(initialData); setLoading(false); return; }
    setLoading(true);
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, initialData]);

  // Open the durable signed-waiver PDF (streamed from R2 through the authenticated
  // endpoint — we fetch it as a blob so the auth token is sent, then open it).
  const viewWaiver = async (submissionId) => {
    setActing('v' + submissionId);
    try {
      const res = await api.get(`${API_BASE_URL}/api/forms/submissions/${submissionId}/signed-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.target = '_blank'; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'No signed copy on file yet.' : 'Could not open the signed waiver.');
    } finally { setActing(''); }
  };

  /**
   * Print every signed form on file for this athlete as one document, so a coach
   * gets the whole file in a single print job rather than opening them one by one.
   * Opens the browser print dialog directly on the merged PDF.
   */
  const printAllForms = async () => {
    setActing('printall');
    try {
      const res = await api.post(
        `${API_BASE_URL}/api/forms/print-pack`,
        { athlete_id: Number(id) },
        // Merging a pack pulls every signed PDF from object storage; the shared
        // client's 15s default is not enough for a whole class.
        { responseType: 'blob', timeout: 120_000 }
      );
      const url = URL.createObjectURL(res.data);
      // A hidden iframe lets us trigger print without a popup blocker in the way.
      const frame = document.createElement('iframe');
      frame.style.position = 'fixed';
      frame.style.right = '0';
      frame.style.bottom = '0';
      frame.style.width = '0';
      frame.style.height = '0';
      frame.style.border = '0';
      frame.src = url;
      frame.onload = () => {
        try {
          frame.contentWindow.focus();
          frame.contentWindow.print();
        } catch {
          window.open(url, '_blank', 'noopener');
        }
      };
      document.body.appendChild(frame);
      // Keep the blob alive long enough for the print dialog to read it.
      setTimeout(() => {
        URL.revokeObjectURL(url);
        frame.remove();
      }, 120000);
    } catch (err) {
      toast.error(
        err.response?.status === 404
          ? 'No signed forms on file for this athlete yet.'
          : 'Could not build the print pack.'
      );
    } finally {
      setActing('');
    }
  };

  // Quick actions (staff/admin only)
  const remind = async (formTemplateId) => {
    setActing('w' + formTemplateId);
    try {
      const { data: r } = await api.post(`${API_BASE_URL}/api/waivers/remind-athlete`, { athlete_id: Number(id), form_template_id: formTemplateId });
      toast.success(`Reminder sent to family${r.notified ? ` (${r.notified})` : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reminder failed');
    } finally { setActing(''); }
  };

  const markPaid = async (invoiceId) => {
    if (!window.confirm('Mark this invoice paid? (records an offline payment)')) return;
    setActing('i' + invoiceId);
    try {
      await api.post(`${API_BASE_URL}/api/invoices/${invoiceId}/pay`, {});
      toast.success('Invoice marked paid');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not mark paid');
    } finally { setActing(''); }
  };

  if (loading) return <div className="ac-wrap"><div className="ac-loading">Loading athlete…</div></div>;
  if (error) return <div className="ac-wrap"><div className="ac-error">{error}</div></div>;
  if (!data) return null;

  const { athlete, classes, waivers, billing, wellness, videos, goals, parents, viewerRole, checkins = [], attendance = null } = data;

  // Group the last 7 days of daily check-in answers by date (most recent first).
  const checkinsByDate = [];
  {
    const seen = {};
    for (const c of checkins) {
      if (!seen[c.date]) { seen[c.date] = { date: c.date, items: [] }; checkinsByDate.push(seen[c.date]); }
      seen[c.date].items.push(c);
    }
  }
  const isStaff = viewerRole === 'admin' || viewerRole === 'coach' || viewerRole === 'superadmin' || viewerRole === 'staff';
  const isAdmin = viewerRole === 'admin' || viewerRole === 'superadmin';
  const initials = (athlete.name || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  const money = (n) => `$${Number(n || 0).toFixed(2)}`;
  const readinessColor = (r) => r == null ? '#9ca3af' : r >= 75 ? '#15803d' : r >= 50 ? '#b45309' : '#b91c1c';
  const waiverStatusLabel = { valid: '✅ Signed', missing: '❌ Missing', expired: '⛔ Expired', expiring_soon: '⚠️ Expiring' };

  return (
    <div className="ac-wrap">
      {!embedded && <button className="ac-back" onClick={() => navigate(-1)}>← Back</button>}

      {/* Header */}
      <div className="ac-header">
        <div className="ac-avatar">{initials}</div>
        <div className="ac-id">
          <h1>{athlete.name} {!athlete.is_active && <span className="ac-inactive">inactive</span>}</h1>
          <div className="ac-meta">
            {athlete.age != null && <span>Age {athlete.age}</span>}
            {athlete.usag_id && <span>USAG #{athlete.usag_id}</span>}
            {athlete.email && <span>{athlete.email}</span>}
            {athlete.phone && <span>{athlete.phone}</span>}
          </div>
          {(athlete.emergency_contact_name || athlete.emergency_contact_phone) && (
            <div className="ac-emergency">🚑 Emergency: <strong>{athlete.emergency_contact_name || '—'}</strong> {athlete.emergency_contact_phone || ''}</div>
          )}
        </div>
        {isStaff && !embedded && (
          <button className="ac-fulllink" onClick={() => navigate(`/athlete-card/${athlete.id}?tab=videos`)}>🎥 Videos →</button>
        )}
      </div>

      {/* Status tiles */}
      <div className="ac-tiles">
        <div className={`ac-tile ${waivers.compliant ? 'good' : waivers.missing > 0 ? 'bad' : 'warn'}`}>
          <span className="ac-tile-label">Waivers</span>
          <span className="ac-tile-value">
            {waivers.required === 0 ? '—' : waivers.compliant ? 'All signed' : `${waivers.missing} missing`}
          </span>
          <span className="ac-tile-sub">{waivers.signed}/{waivers.required} signed{waivers.expiring > 0 ? ` · ${waivers.expiring} expiring` : ''}</span>
        </div>
        <div className={`ac-tile ${billing.outstanding > 0 ? 'bad' : 'good'}`}>
          <span className="ac-tile-label">Balance Due</span>
          <span className="ac-tile-value">{money(billing.outstanding)}</span>
          <span className="ac-tile-sub">{billing.outstanding > 0 ? 'outstanding' : 'paid up'}</span>
        </div>
        <div className="ac-tile neutral">
          <span className="ac-tile-label">Monthly Tuition</span>
          <span className="ac-tile-value">{money(billing.monthly_recurring)}</span>
          <span className="ac-tile-sub">{classes.length} class{classes.length !== 1 ? 'es' : ''}</span>
        </div>
        <div className="ac-tile neutral">
          <span className="ac-tile-label">Readiness</span>
          <span className="ac-tile-value" style={{ color: readinessColor(wellness.latest?.readiness_score) }}>
            {wellness.latest?.readiness_score != null ? `${wellness.latest.readiness_score}` : '—'}
          </span>
          <span className="ac-tile-sub">{wellness.latest ? new Date(wellness.latest.checkin_date).toLocaleDateString() : 'no check-ins'}</span>
        </div>
        <div className={`ac-tile ${attendance?.rate == null ? 'neutral' : attendance.rate >= 85 ? 'good' : attendance.rate >= 70 ? 'warn' : 'bad'}`}>
          <span className="ac-tile-label">Attendance</span>
          <span className="ac-tile-value">{attendance?.rate != null ? `${attendance.rate}%` : '—'}</span>
          <span className="ac-tile-sub">{attendance?.total ? `${attendance.absent} missed / ${attendance.total}` : 'no records'}</span>
        </div>
      </div>

      {/* Safety-forward medical summary (allergies / active injuries / clearance) */}
      <MedicalSafetyCard athleteId={athlete.id} viewerRole={viewerRole} />

      {/* Section grid */}
      <div className="ac-grid">
        {/* Classes */}
        <div className="ac-card">
          <h2>📚 Classes</h2>
          {classes.length === 0 ? <p className="ac-empty">Not enrolled in any class.</p> : (
            <ul className="ac-list">
              {classes.map(c => (
                <li key={c.class_id}>
                  <span>{c.class_name}{c.level ? ` · ${c.level}` : ''}{c.billing_option ? ` · ${c.billing_option}` : ''}</span>
                  <span className="ac-amt">{money(c.net_tuition)}/{c.payment_frequency === 'monthly' ? 'mo' : c.payment_frequency === 'quarterly' ? 'qtr' : 'yr'}{c.discount_reason ? ` (${c.discount_reason})` : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Waivers */}
        <div className="ac-card">
          <div className="ac-card-head">
            <h2>📝 Waivers &amp; Forms</h2>
            {/* One print job for the athlete's whole file — what a coach actually
                needs before a meet. Only offered when there is something to print. */}
            {viewerRole !== 'athlete' && waivers.items.some(w => w.status === 'valid' && w.has_signed_pdf) && (
              <button className="ac-action" disabled={acting === 'printall'} onClick={printAllForms}>
                {acting === 'printall' ? '…' : '🖨️ Print all'}
              </button>
            )}
          </div>
          {waivers.items.length === 0 ? <p className="ac-empty">No required forms.</p> : (
            <ul className="ac-list">
              {waivers.items.map(w => (
                <li key={w.form_template_id}>
                  <span>{w.form_name}</span>
                  <span className="ac-li-right">
                    <span className={`ac-badge s-${w.status}`}>{waiverStatusLabel[w.status] || w.status}</span>
                    {w.status === 'valid' && w.has_signed_pdf && w.form_submission_id && viewerRole !== 'athlete' && (
                      <button className="ac-action" disabled={acting === 'v' + w.form_submission_id} onClick={() => viewWaiver(w.form_submission_id)}>
                        {acting === 'v' + w.form_submission_id ? '…' : '📄 View'}
                      </button>
                    )}
                    {isStaff && ['missing', 'expired', 'expiring_soon'].includes(w.status) && (
                      <button className="ac-action" disabled={acting === 'w' + w.form_template_id} onClick={() => remind(w.form_template_id)}>
                        {acting === 'w' + w.form_template_id ? '…' : '📧 Remind'}
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Billing */}
        <div className="ac-card">
          <h2>🧾 Billing</h2>
          <div className="ac-billing-head">
            <span>Outstanding</span><strong className={billing.outstanding > 0 ? 'due' : ''}>{money(billing.outstanding)}</strong>
          </div>
          {billing.invoices.length === 0 ? <p className="ac-empty">No invoices.</p> : (
            <ul className="ac-list">
              {billing.invoices.slice(0, 6).map(inv => (
                <li key={inv.id}>
                  <span>{inv.description || inv.invoice_type} <em className="ac-due">· {new Date(inv.due_date).toLocaleDateString()}</em></span>
                  <span className="ac-li-right">
                    <span className={`ac-amt inv-${inv.status}`}>{money(inv.amount_due)} · {inv.status}</span>
                    {isAdmin && inv.status !== 'paid' && inv.status !== 'voided' && (
                      <button className="ac-action" disabled={acting === 'i' + inv.id} onClick={() => markPaid(inv.id)}>
                        {acting === 'i' + inv.id ? '…' : 'Mark paid'}
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Wellness */}
        <div className="ac-card">
          <h2>🧘 Wellness</h2>
          {wellness.recent.length === 0 ? <p className="ac-empty">No check-ins yet.</p> : (
            <ul className="ac-list">
              {wellness.recent.map((w, i) => (
                <li key={i}>
                  <span>{new Date(w.checkin_date).toLocaleDateString()}</span>
                  <span className="ac-amt" style={{ color: readinessColor(w.readiness_score) }}>
                    Readiness {w.readiness_score ?? '—'}{w.soreness != null ? ` · sore ${w.soreness}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Daily check-ins (post-attendance questions, last 7 days) */}
        {checkins.length > 0 && (
          <div className="ac-card">
            <h2>🩹 Daily Check-ins</h2>
            <ul className="ac-list">
              {checkinsByDate.map(day => (
                <li key={day.date} style={{ display: 'block' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.82rem', marginBottom: 3 }}>
                    {new Date(day.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {day.items.map((c, i) => (
                      <span key={i} title={c.prompt} style={{
                        fontSize: '0.78rem', padding: '1px 8px', borderRadius: 999,
                        background: c.flagged ? '#fee2e2' : '#f1f5f9',
                        color: c.flagged ? '#991b1b' : '#475569',
                        fontWeight: c.flagged ? 700 : 400,
                      }}>
                        {c.prompt}: {c.qtype === 'bodymap'
                          ? `${String(c.response || '').split(',').filter(Boolean).length} area(s)`
                          : (c.qtype === 'scale' || c.qtype === 'faces') ? `${c.response}/5` : c.response}
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Videos */}
        <div className="ac-card">
          <h2>🎥 Videos <span className="ac-count">{videos.count}</span></h2>
          {videos.items.length === 0 ? <p className="ac-empty">No videos yet.</p> : (
            <ul className="ac-list">
              {videos.items.map(v => (
                <li key={v.id}>
                  <span>{v.event || v.file_name || `Video ${v.id}`} <em className="ac-due">· {new Date(v.created_at).toLocaleDateString()}</em></span>
                  <span className={`ac-badge vs-${v.analysis_status}`}>{v.analysis_status || 'uploaded'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Goals */}
        <div className="ac-card">
          <h2>🎯 Goals</h2>
          {goals.items.length === 0 ? <p className="ac-empty">No goals set.</p> : (
            <ul className="ac-list">
              {goals.items.map(g => (
                <li key={g.id}>
                  <span>{g.title}{g.category ? ` · ${g.category}` : ''}</span>
                  <span className="ac-amt">{g.progress_percentage != null ? `${g.progress_percentage}%` : g.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Guardians */}
        {parents && parents.length > 0 && (
          <div className="ac-card">
            <h2>👨‍👩‍👧 Guardians</h2>
            <ul className="ac-list">
              {parents.map((p, i) => (
                <li key={i}>
                  <span>{p.name}{p.is_primary ? ' ⭐' : ''} <em className="ac-due">· {p.relationship || 'parent'}</em></span>
                  <span className="ac-amt">{p.phone || p.email || ''}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default AthleteCard;
